import {
    AssessmentSchedule,
    DailyRecurrence,
    DayOfWeek,
    MonthlyRecurrence,
    MonthlyScheduleType,
    WeeklyRecurrence
} from "../../models";

import _ from "lodash";
import { format } from "date-fns";

export class ScheduleToHumanReadableConverter {

    public convert(schedule: Partial<AssessmentSchedule>): string {
        const { startDate, endDate, recurrence } = schedule;
        try {
            if (!recurrence || startDate == undefined) {
                throw new Error(`Error: Schedule recurrence or start date is not set`);
            }
            const { type, dailyRecurrence, weeklyRecurrence, monthlyRecurrence } = recurrence;
            switch (type) {
                case "DAILY":
                    return `${this.convertDailyRecurrence(dailyRecurrence ?? undefined)} from ${format(new Date(startDate), "yyyy-MM-dd")}${endDate ? ` to ${format(new Date(endDate), "yyyy-MM-dd")}` : ""}`;
                case "WEEKLY":
                    return `${this.convertWeeklyRecurrence(weeklyRecurrence ?? undefined)} from ${format(new Date(startDate), "yyyy-MM-dd")}${endDate ? ` to ${format(new Date(endDate), "yyyy-MM-dd")}` : ""}`;
                case "MONTHLY":
                    return `${this.convertMonthlyRecurrence(monthlyRecurrence ?? undefined)} from ${format(new Date(startDate), "yyyy-MM-dd")}${endDate ? ` to ${format(new Date(endDate), "yyyy-MM-dd")}` : ""}`;
                default:
                    throw new Error(`Error: Unknown recurrence type: ${type}`);
            }
        } catch (error) {
            return "";
        }
    }

    private convertDailyRecurrence(dailyRecurrence: DailyRecurrence | undefined): string {
        if (!dailyRecurrence || dailyRecurrence.interval == undefined) {
            throw new Error(`Error: Daily recurrence is not set`);
        }
        return `Every ${dailyRecurrence?.interval} day(s)`;
    }

    private convertWeeklyRecurrence(weeklyRecurrence: WeeklyRecurrence | undefined): string {
        if (!weeklyRecurrence || weeklyRecurrence.interval == undefined || weeklyRecurrence.daysOfWeek == undefined || weeklyRecurrence.daysOfWeek.length === 0) {
            throw new Error(`Error: Weekly recurrence is not set`);
        }
        const formatter = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' });
        return `Every ${weeklyRecurrence.interval} week(s) on ${formatter.format(weeklyRecurrence.daysOfWeek.map(day => _.capitalize(day as DayOfWeek)))}`;
    }


    private convertMonthlyRecurrence(monthlyRecurrence: MonthlyRecurrence | undefined): string {
        if (!monthlyRecurrence || monthlyRecurrence.interval == undefined) {
            throw new Error(`Error: Monthly recurrence is not set`);
        }
        switch (monthlyRecurrence.type) {
            case MonthlyScheduleType.DAY_OF_MONTH:
                if (monthlyRecurrence.dayOfMonth == undefined) {
                    throw new Error(`Error: Monthly recurrence day of month is not set`);
                }
                if (!this.isNumberString(monthlyRecurrence.dayOfMonth) && monthlyRecurrence.dayOfMonth !== "L") {
                    throw new Error(`Error: Monthly recurrence day of month is not a number or "L"`);
                }
                if (monthlyRecurrence.dayOfMonth === "L") {
                    return `Every ${monthlyRecurrence?.interval} month(s) on the last day`;
                }
                return `Every ${monthlyRecurrence?.interval} month(s) on the ${this.numberToOrdinal(parseInt(monthlyRecurrence.dayOfMonth))}`;
            case MonthlyScheduleType.NTH_WEEK_DAY:
                if (monthlyRecurrence.nthWeekDay == undefined || monthlyRecurrence.nthWeekDay.day == undefined || monthlyRecurrence.nthWeekDay.weekNumber == undefined) {
                    throw new Error(`Error: Monthly recurrence day of week is not set`);
                }
                if (!this.isNumberString(monthlyRecurrence.nthWeekDay.weekNumber) && monthlyRecurrence.nthWeekDay.weekNumber !== "L") {
                    throw new Error(`Error: Monthly recurrence week number is not a number or "L"`);
                }
                if (monthlyRecurrence.nthWeekDay.weekNumber === "L") {
                    return `Every ${monthlyRecurrence?.interval} month(s) on the last ${_.capitalize(monthlyRecurrence.nthWeekDay.day)} of the month`;
                }
                return `Every ${monthlyRecurrence?.interval} month(s) on the ${this.numberToOrdinal(parseInt(monthlyRecurrence.nthWeekDay.weekNumber))} ${_.capitalize(monthlyRecurrence.nthWeekDay.day)} of the month`;
            default:
                throw new Error(`Error: Unknown monthly recurrence type: ${monthlyRecurrence?.type}`);
        }
    }

    private numberToOrdinal(n: number): string {
        const s = ["th", "st", "nd", "rd"];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
    }


    private isNumberString(value: string): boolean {
        return /^\d+$/.test(value);
    }
}

export const scheduleToHumanReadableConverter = new ScheduleToHumanReadableConverter();